import type { CSSProperties } from 'react';
import { cn } from '$lib/lib/cn';

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  className?: string;
  // Pass through for cell-aligned skeletons inside flex layouts.
  style?: CSSProperties;
  // Renders the bar at half opacity — useful for "secondary" lines (counts,
  // captions) so a card skeleton has visual hierarchy.
  muted?: boolean;
}

// Loading placeholder bar. Pulse honours prefers-reduced-motion via
// motion-safe — Tailwind translates that to a media query.
export function Skeleton({ width, height = 12, className, style, muted = false }: SkeletonProps) {
  const sizeStyle: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style
  };
  return (
    <span
      role="presentation"
      aria-hidden
      className={cn(
        'block rounded-xs bg-surface-3 motion-safe:animate-pulse',
        muted && 'opacity-60',
        className
      )}
      style={sizeStyle}
    />
  );
}

export interface SkeletonCardProps {
  className?: string;
}

// Stand-in for a portal status card while the dashboard fetches health data.
// Layout matches the live PortalCard well enough that swapping the live data
// in doesn't cause visible reflow.
export function SkeletonCard({ className }: SkeletonCardProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn(
        'rounded border border-rule bg-surface p-4 shadow-elev-1',
        className
      )}
    >
      <div className="flex items-center gap-2">
        <Skeleton width={8} height={8} className="rounded-full" />
        <Skeleton width="50%" height={14} />
        <Skeleton width={56} height={16} className="ml-auto" />
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <Skeleton width="80%" height={11} muted />
        <Skeleton width="60%" height={11} muted />
      </div>
    </div>
  );
}

export interface SkeletonTableRowProps {
  // Width hints per column so the skeleton row visually echoes the recent-runs
  // table (Portal · Started · Duration · Status · Found · Saved · Errors).
  columnWidths?: Array<number | string>;
  className?: string;
}

const defaultRecentRunWidths: Array<number | string> = ['22%', '18%', '12%', '14%', 64, 64, 56];

export function SkeletonTableRow({
  columnWidths = defaultRecentRunWidths,
  className
}: SkeletonTableRowProps) {
  return (
    <tr role="presentation" aria-hidden className={cn('h-row', className)}>
      {columnWidths.map((w, i) => (
        <td key={i} className="px-3">
          <Skeleton width={w} height={11} />
        </td>
      ))}
    </tr>
  );
}
